/**
 * collection-sold-out-sort.js
 * ---------------------------------------------------------------------------
 * Displays fully sold-out products at the very end of the WHOLE collection
 * (i.e. on the last page / pages), while keeping the backend sort order for
 * in-stock products.
 *
 * Why this exists:
 *   Shopify paginates `collection.products` on the server *before* the theme
 *   sees them, so Liquid can only reorder within a single page. To move
 *   sold-out products onto the last page(s) of the entire collection, we take
 *   over the page content client-side.
 *
 * How it works:
 *   The collection is treated as two streams in the currently selected sort:
 *     - in-stock      -> ?filter.v.availability=1
 *     - out-of-stock  -> ?filter.v.availability=0
 *   Native pagination of the (mixed) collection already produces exactly
 *   ceil(total / perPage) pages, which equals the page count we want. So we
 *   keep the server-rendered pagination and only swap WHICH products each page
 *   shows: in-stock products fill the early pages, sold-out products fill the
 *   tail. For each page we fetch the relevant slice(s) of each stream via the
 *   Section Rendering API and stitch them together.
 *
 *   CAVEAT the code has to handle: filter.v.availability is VARIANT-level.
 *   The "0" stream therefore also contains products that still have other
 *   variants in stock (they appear in BOTH streams). Treating that stream as
 *   "the sold-out products" would push partially available products into the
 *   sold-out tail and duplicate them. So the tail is built by scanning the "0"
 *   stream and keeping only cards whose product-level `available` attribute
 *   (rendered by product-card.liquid) is "false". The true sold-out count
 *   (products_count - in-stock count) is computed server-side and passed via
 *   data-sold-out-count; data-oos-stream-count is the raw size of the "0"
 *   stream, used only to know how many pages the scan may need.
 *
 * Requirements / degradation:
 *   - Needs the native "Availability" filter enabled in Search & Discovery.
 *     If it is missing, or already applied by the shopper, or the collection
 *     has no sold-out products (or no in-stock products), the script does
 *     nothing and the native behavior is kept (data-sold-out-sort="false").
 *   - Without JS, the server still renders every product (per-page sold-out
 *     ordering from the Liquid fallback). This is the SEO/no-JS fallback.
 *   - Promo blocks injected into the product list are suppressed by Shopify
 *     whenever a filter is active, so the fetched streams never contain them.
 *     The ones the server rendered into the live page are captured before the
 *     rebuild and put back at their original positions on page 1. A shopper who
 *     enters on page 2+ never receives them from the server either, so in that
 *     case they are taken from the native page 1 render instead.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__soldOutSortInit) return;
  window.__soldOutSortInit = true;

  var AVAILABILITY_PARAM = 'filter.v.availability';
  var SORTED_MARKER = 'data-sold-out-sorted';
  var responseCache = new Map();
  var promoStore = new WeakMap();

  /**
   * Read and validate the configuration rendered by main-collection.liquid.
   * Returns null when the feature is not applicable for the current state.
   */
  function readConfig(collectionEl) {
    if (!collectionEl || collectionEl.getAttribute('data-sold-out-sort') !== 'true') {
      return null;
    }

    var inStock = parseInt(collectionEl.getAttribute('data-in-stock-count'), 10);
    var soldOut = parseInt(collectionEl.getAttribute('data-sold-out-count'), 10);
    var oosStream = parseInt(collectionEl.getAttribute('data-oos-stream-count'), 10);
    var perPage = parseInt(collectionEl.getAttribute('data-per-page'), 10);
    var sectionId = collectionEl.getAttribute('data-section-id');

    if (!inStock || !soldOut || !oosStream || !perPage || !sectionId) return null;

    var total = inStock + soldOut;
    var totalPages = Math.ceil(total / perPage);

    // Only worth doing when the collection spans more than one page; a single
    // page is already correctly ordered by the Liquid fallback.
    if (totalPages <= 1) return null;

    return {
      inStock: inStock,
      soldOut: soldOut,
      oosStream: oosStream,
      perPage: perPage,
      total: total,
      totalPages: totalPages,
      sectionId: sectionId
    };
  }

  /** Current page number from the URL (1-based). */
  function currentPage() {
    var page = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    return page && page > 0 ? page : 1;
  }

  /**
   * Build a Section Rendering URL for one page, preserving the active sort and
   * any other active filters. Pass `availability` as 1 / 0 to target a stream,
   * or null to fetch the native (mixed) section (used only for its pagination).
   */
  function sectionUrl(config, availability, page) {
    var url = new URL(window.location.pathname, window.location.origin);
    var current = new URLSearchParams(window.location.search);

    current.forEach(function (value, key) {
      if (key === 'page' || key === 'section_id' || key === AVAILABILITY_PARAM) return;
      url.searchParams.append(key, value);
    });

    if (availability !== null) url.searchParams.set(AVAILABILITY_PARAM, String(availability));
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', config.sectionId);
    return url.toString();
  }

  function fetchDocument(url) {
    if (responseCache.has(url)) return responseCache.get(url);

    var promise = fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      })
      .catch(function (error) {
        responseCache.delete(url); // allow retry on a later navigation
        throw error;
      });

    responseCache.set(url, promise);
    return promise;
  }

  /** Return the product-card elements for a contiguous range of one stream. */
  function fetchCards(config, availability, startIndex, endIndex) {
    if (startIndex >= endIndex) return Promise.resolve([]);

    var perPage = config.perPage;
    var firstPage = Math.floor(startIndex / perPage) + 1;
    var lastPage = Math.floor((endIndex - 1) / perPage) + 1;
    var pageFetches = [];

    for (var page = firstPage; page <= lastPage; page++) {
      pageFetches.push(fetchDocument(sectionUrl(config, availability, page)));
    }

    return Promise.all(pageFetches).then(function (docs) {
      var cards = [];
      docs.forEach(function (doc) {
        var list = doc.querySelector('product-list');
        if (!list) return;
        cards = cards.concat(Array.prototype.slice.call(list.querySelectorAll(':scope > product-card')));
      });

      // `cards` now covers native pages [firstPage..lastPage]; slice to the
      // exact range requested relative to that block's starting index.
      var offset = (firstPage - 1) * perPage;
      return cards.slice(startIndex - offset, endIndex - offset);
    });
  }

  /**
   * Return the product-card elements for a contiguous range of the FULLY
   * sold-out products (in the current sort order).
   *
   * The availability=0 stream is variant-level, so it also contains partially
   * available products at unpredictable positions. Page slices of that stream
   * therefore cannot be addressed directly: we scan it from the start, keep
   * only cards whose product-level `available` attribute is "false", and stop
   * as soon as the requested range is covered. Documents are cached by
   * fetchDocument, so revisiting pages costs nothing.
   */
  function fetchSoldOutCards(config, startIndex, endIndex) {
    if (startIndex >= endIndex) return Promise.resolve([]);

    var maxPages = Math.max(1, Math.ceil(config.oosStream / config.perPage));
    var collected = [];

    function scanPage(page) {
      if (page > maxPages || collected.length >= endIndex) {
        return Promise.resolve(collected.slice(startIndex, endIndex));
      }

      return fetchDocument(sectionUrl(config, 0, page)).then(function (doc) {
        var list = doc.querySelector('product-list');

        if (list) {
          Array.prototype.slice.call(list.querySelectorAll(':scope > product-card')).forEach(function (card) {
            if (card.getAttribute('available') === 'false') collected.push(card);
          });
        }

        return scanPage(page + 1);
      });
    }

    return scanPage(1);
  }

  /**
   * Resolve the ordered list of product-card elements for a given global page.
   * Positions 0..inStock-1 are in-stock; inStock..total-1 are fully sold out.
   */
  function buildPageCards(config, globalPage) {
    var lo = (globalPage - 1) * config.perPage;
    var hi = Math.min(globalPage * config.perPage, config.total);

    var inStart = Math.max(lo, 0);
    var inEnd = Math.min(hi, config.inStock);
    var outStart = Math.max(lo, config.inStock);
    var outEnd = hi;

    return Promise.all([
      fetchCards(config, 1, inStart, inEnd),
      fetchSoldOutCards(config, outStart - config.inStock, outEnd - config.inStock)
    ]).then(function (groups) {
      return groups[0].concat(groups[1]);
    });
  }


  function collectPromos(list, adopt) {
    var promos = [];

    Array.prototype.slice.call(list.children).forEach(function (child, index) {
      if (child.classList && child.classList.contains('product-list__promo')) {
        promos.push({ node: adopt ? document.importNode(child, true) : child, index: index });
      }
    });

    return promos;
  }


  function capturePromos(productList, page) {
    if (promoStore.has(productList)) return;

    promoStore.set(productList, page === 1 ? collectPromos(productList, false) : null);
  }


  function resolvePromos(config, productList, page) {
    if (page !== 1) return Promise.resolve([]);

    var captured = promoStore.get(productList);
    if (captured) return Promise.resolve(captured);

    return fetchDocument(sectionUrl(config, null, 1))
      .then(function (doc) {
        var list = doc.querySelector('product-list');
        var promos = list ? collectPromos(list, true) : [];

        promoStore.set(productList, promos);
        return promos;
      })
      .catch(function () {
        return []; 
      });
  }

  function renderCards(productList, cards, promos) {
    var fragment = document.createDocumentFragment();
    cards.forEach(function (card) {
      fragment.appendChild(document.importNode(card, true));
    });
    productList.replaceChildren(fragment);

    promos.forEach(function (promo) {
      productList.insertBefore(promo.node, productList.children[promo.index] || null);
    });
  }

  /**
   * Sync the pagination UI (current-page highlight, prev/next, ellipsis) to the
   * given page. The native mixed collection paginates over the same total, so
   * its pagination markup for page `g` is exactly the nav we want.
   */
  function syncPagination(config, collectionEl, page) {
    var existingNav = collectionEl.querySelector('.pagination');
    if (!existingNav) return;

    fetchDocument(sectionUrl(config, null, page))
      .then(function (doc) {
        var freshNav = doc.querySelector('.pagination');
        var liveNav = collectionEl.querySelector('.pagination');
        if (freshNav && liveNav) {
          liveNav.replaceChildren.apply(liveNav, Array.prototype.slice.call(document.importNode(freshNav, true).childNodes));
        }
      })
      .catch(function () {});
  }

  // True only while we are writing our own cards into the product list, so the
  // mutation observer can ignore the resulting (self-inflicted) DOM changes.
  var applying = false;

  /**
   * Apply the global ordering for the current page to the product list inside
   * the live `#collection` element. No-op when the feature is not applicable
   * or the current content is already ours for this page.
   */
  function apply() {
    var collectionEl = document.getElementById('collection');
    var config = collectionEl && readConfig(collectionEl);
    if (!config) return;

    var productList = collectionEl.querySelector('product-list');
    var page = Math.min(currentPage(), config.totalPages);
    if (!productList || productList.getAttribute(SORTED_MARKER) === String(page)) return;

    capturePromos(productList, page);

    applying = true;
    collectionEl.classList.add('is-loading');

    Promise.all([buildPageCards(config, page), resolvePromos(config, productList, page)])
      .then(function (results) {
        var cards = results[0];

        if (cards.length && document.getElementById('collection') === collectionEl) {
          renderCards(productList, cards, results[1]);
          productList.setAttribute(SORTED_MARKER, String(page));
          syncPagination(config, collectionEl, page);
        }
      })
      .catch(function () {
        /* Leave the native server-rendered content in place on failure. */
      })
      .finally(function () {
        collectionEl.classList.remove('is-loading');
        applying = false;
      });
  }

  function init() {
    var collectionEl = document.getElementById('collection');
    if (!collectionEl) return;

    var section = collectionEl.closest('.shopify-section') || document.body;

    // React to facet/sort changes: the theme re-renders the whole section
    // (resetting to native, mixed-order page 1), so we reorder the new state.
    // `applying` suppresses the mutations we cause ourselves.
    var observer = new MutationObserver(function () {
      if (!applying) apply();
    });
    observer.observe(section, { childList: true });

    // Intercept pagination clicks so a page change reorders instead of doing a
    // native (mixed-order) section swap. Capture phase runs before the
    // <facet-link> handler, letting us stop it.
    document.addEventListener(
      'click',
      function (event) {
        var collection = document.getElementById('collection');
        if (!collection || collection.getAttribute('data-sold-out-sort') !== 'true') return;

        var link = event.target.closest('.pagination a[href]');
        if (!link || !collection.contains(link)) return;

        var targetPage = parseInt(new URL(link.href, window.location.origin).searchParams.get('page'), 10);
        if (!targetPage) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        var url = new URL(window.location.href);
        url.searchParams.set('page', String(targetPage));
        history.pushState({}, '', url.toString());

        var productList = collection.querySelector('product-list');
        if (productList) productList.removeAttribute(SORTED_MARKER);
        apply();

        var top = collection.querySelector('.collection__results') || collection;
        if (top && top.scrollIntoView) top.scrollIntoView({ block: 'start', behavior: 'smooth' });
      },
      true
    );

    window.addEventListener('popstate', function () {
      var collection = document.getElementById('collection');
      var productList = collection && collection.querySelector('product-list');
      if (productList) productList.removeAttribute(SORTED_MARKER);
      apply();
    });

    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
